import { findPov } from './presets.js';
/**
 * Every photograph of a garment, the primary one first.
 *
 * The primary photograph is stored on its own field rather than as the first
 * entry of `views`, so that a garment uploaded before views existed still has
 * a picture. This is the one place that difference is papered over.
 */
export function garmentViews(garment) {
    const extra = garment.views ?? [];
    // A garment whose primary photo was also tagged must not appear twice.
    const rest = extra.filter((v) => v.assetId !== garment.assetId);
    const primary = extra.find((v) => v.assetId === garment.assetId);
    return [{ assetId: garment.assetId, kind: primary?.kind ?? 'front' }, ...rest];
}
/**
 * Which photograph of this garment to attach when shooting `povId`.
 *
 * Falls back to the front, and then to the primary photograph: a missing back
 * view is a worse reference, never a missing one. The care label is never
 * chosen — it shows a strip of printed cotton, not the garment.
 */
export function referenceAssetFor(garment, povId) {
    const want = povId ? findPov(povId)?.garmentView : undefined;
    return pickView(garment, want);
}
/**
 * The picture of a piece a render is drawn from: its ghost photograph of the
 * side the framing sees, when the studio has made one, and its own
 * photograph otherwise. A close-up framing is always shot against the
 * close-up itself.
 */
export function renderAssetFor(garment, povId) {
    const want = povId ? findPov(povId)?.garmentView : undefined;
    if (want === 'detail' || want === 'label')
        return referenceAssetFor(garment, povId);
    const of = want === 'back' ? 'back' : want === 'side' ? 'side' : 'front';
    return garment.ghosts?.find((g) => g.of === of)?.assetId ?? referenceAssetFor(garment, povId);
}
export function pickView(garment, want) {
    const views = garmentViews(garment).filter((v) => v.kind !== 'label');
    if (!views.length)
        return garment.assetId;
    const wanted = want && want !== 'label' ? views.find((v) => v.kind === want) : undefined;
    if (wanted)
        return wanted.assetId;
    /*
     * A side framing shows the seam line down the edge of the garment, which a
     * back photograph carries and a front one does not. So a side view falls to
     * the back before it falls to the front; everything else goes straight to
     * the front.
     */
    if (want === 'side') {
        const back = views.find((v) => v.kind === 'back');
        if (back)
            return back.assetId;
    }
    return views.find((v) => v.kind === 'front')?.assetId ?? views[0].assetId;
}
/**
 * How many photographs of people one render is sent, everything included:
 * her portraits and figure, the pose photograph, an approved frame, a
 * previous attempt. Gemini's Pro image model holds a likeness across up to
 * five; past that the faces blend, and a roll sent six came back as someone
 * else in every frame.
 */
export const MAX_PEOPLE_REFERENCES = 5;
/**
 * How many photographs of things one render reproduces faithfully: the
 * pieces, the place, the close-ups. Six, by the same model's account; the
 * pieces and the place always go, and the close-ups fill what is left.
 */
export const MAX_OBJECT_REFERENCES = 6;
/**
 * The most images one render takes, whatever they show: the limit of
 * Gemini's Pro image model.
 */
export const MAX_RENDER_IMAGES = 14;
const PIECE_SIDES = ['front', 'back', 'left', 'right'];
/**
 * Every image of every piece for one render: each side it was photographed
 * from — its own photograph, and its ghost when it has both — and every
 * close-up of its details. The care label never goes.
 *
 * The photograph is the piece as it really is, and comes first. The ghost
 * is a clean picture of its shape, but it is a render: it can loosen an
 * embroidery the photograph holds exactly, and a jacket copied from it came
 * back "in the style of" the one photographed. It goes second, for the cut
 * and the outline; a side with no photograph is sent its ghost alone.
 *
 * All of them go when they fit in `room`. When they do not, the front and
 * the back of every piece go first, then their ghosts, then a profile of
 * each, then the close-ups piece by piece, then the rest. They come back
 * grouped by piece: each side, photograph then ghost, then its close-ups.
 */
export function pieceImages(garments, room, 
/**
 * The side the camera sees, when it is known: its images go first, ghost
 * and photograph both before anything else, so the side that shows is the
 * one sent whole.
 */
seen) {
    const order = seen === 'back'
        ? ['back', 'front', 'left', 'right']
        : seen === 'side'
            ? ['left', 'right', 'front', 'back']
            : PIECE_SIDES;
    const isSeen = (side) => seen === 'side' ? side === 'left' || side === 'right' : side === (seen ?? 'front');
    const perPiece = garments.map((garment, index) => {
        const views = garmentViews(garment);
        const sides = [];
        for (const side of order) {
            const ghost = (garment.ghosts ?? []).find((g) => (g.of === 'side' ? (g.side ?? 'left') : g.of) === side);
            const view = views.find((v) => side === 'left' || side === 'right'
                ? v.kind === 'side' && (v.side ?? 'left') === side
                : v.kind === side);
            const photo = view
                ? { index, assetId: view.assetId, kind: side, ghost: false }
                : undefined;
            const shape = ghost
                ? { index, assetId: ghost.assetId, kind: side, ghost: true }
                : undefined;
            if (photo)
                sides.push({ side, first: photo, second: shape });
            else if (shape)
                sides.push({ side, first: shape });
        }
        const details = views
            .filter((v) => v.kind === 'detail')
            .map((v) => ({
            index,
            assetId: v.assetId,
            kind: 'detail',
            ghost: false,
            ...(v.note ? { note: v.note } : {}),
        }));
        return { sides, details };
    });
    const isProfile = (s) => s.side === 'left' || s.side === 'right';
    const tiers = [
        // The side the camera sees, whole, before anything else.
        ...(seen
            ? [
                perPiece.flatMap((p) => p.sides
                    .filter((s) => isSeen(s.side))
                    .slice(0, seen === 'side' ? 1 : 2)
                    .flatMap((s) => (s.second ? [s.first, s.second] : [s.first]))),
            ]
            : []),
        perPiece.flatMap((p) => p.sides.filter((s) => !isProfile(s)).map((s) => s.first)),
        perPiece.flatMap((p) => p.sides.filter((s) => !isProfile(s) && s.second).map((s) => s.second)),
        perPiece.flatMap((p) => p.sides
            .filter(isProfile)
            .slice(0, 1)
            .map((s) => s.first)),
    ];
    const mostDetails = Math.max(0, ...perPiece.map((p) => p.details.length));
    for (let i = 0; i < mostDetails; i++)
        tiers.push(perPiece.flatMap((p) => (p.details[i] ? [p.details[i]] : [])));
    tiers.push(perPiece.flatMap((p) => p.sides
        .filter(isProfile)
        .slice(1)
        .map((s) => s.first)));
    tiers.push(perPiece.flatMap((p) => p.sides.filter((s) => isProfile(s) && s.second).map((s) => s.second)));
    const chosen = new Set();
    for (const tier of tiers) {
        for (const image of tier) {
            if (chosen.size >= Math.max(0, room))
                break;
            chosen.add(image);
        }
    }
    return perPiece.flatMap((p) => [...p.sides.flatMap((s) => (s.second ? [s.first, s.second] : [s.first])), ...p.details].filter((image) => chosen.has(image)));
}
/**
 * Every other photograph of each piece, attached after everything else: the
 * sides the main picture does not show, then the close-ups of its details.
 * The care label never goes — it is read once, for the fabric — and a view
 * already sent as the piece's main picture is not sent twice.
 *
 * The analyst sees every photograph once and writes down what it read, but a
 * written "brass zip with a ring pull" is a hope; the photograph of that zip,
 * or of the back yoke, beside the piece, is what the renderer copies.
 * `index` is the piece's place among `garments`, so the prompt can name it.
 */
export function extraGarmentReferences(garments, povId) {
    const out = [];
    garments.forEach((garment, index) => {
        const main = referenceAssetFor(garment, povId);
        const rest = garmentViews(garment).filter((v) => v.kind !== 'label' && v.assetId !== main);
        for (const view of rest)
            if (view.kind !== 'detail')
                out.push({ index, garment, view });
        for (const view of rest)
            if (view.kind === 'detail')
                out.push({ index, garment, view });
    });
    return out;
}
/** How each view is introduced to the analyst. */
export const VIEW_CAPTIONS = {
    front: 'front view',
    back: 'back view',
    side: 'side view',
    detail: 'close detail',
    label: 'care label',
};
/** The order a model's turnaround is shown and generated in. */
export const MODEL_VIEW_KINDS = ['front', 'profile', 'back'];
/**
 * Every full-body photograph of the model to attach when shooting `povId`,
 * the side the camera sees first: the back for a back framing, the profile
 * for a side one, the front for everything else; then the other sides, so
 * her build, her hair and how she stands are copied from all round rather
 * than invented for the sides the first one hides. A close-up of the fabric
 * shows no person and gets none.
 */
export function modelViewsFor(model, povId) {
    const views = model.views ?? [];
    if (!views.length)
        return [];
    const sees = povId ? findPov(povId)?.garmentView : undefined;
    if (sees === 'detail' || sees === 'label')
        return [];
    const want = sees === 'back' ? 'back' : sees === 'side' ? 'profile' : 'front';
    const first = views.find((v) => v.kind === want) ??
        views.find((v) => v.kind === 'front') ??
        views.find((v) => v.kind === 'profile') ??
        views[0];
    const rest = MODEL_VIEW_KINDS.flatMap((kind) => views.filter((v) => v.kind === kind && v !== first));
    return [first, ...rest];
}
/** The one full-body photograph that matches the framing; see `modelViewsFor`. */
export function modelViewFor(model, povId) {
    return modelViewsFor(model, povId)[0];
}
//# sourceMappingURL=garments.js.map